/* Sticky Add-to-Cart bar (Claude) - sync + reveal + proxy-add.
 *
 * Design contract (AllShades_StickyBar_Guidebook_2026-07-03):
 * - The bar holds NO variant state: it mirrors the main product form.
 * - Sticky -> main drive: click the input[data-variant-id] inside the LIVE
 *   <variant-picker> (re-queried fresh on every interaction, because Section
 *   Rendering replaces those nodes on each variant change).
 * - Main -> sticky sync: delegated listeners on document for variantUpdate /
 *   variantSelected, with a fallback read of the live hidden input[name="id"].
 * - Add to cart: proxy-click the main add-to-cart button
 *   (button[id^="BuyButtons-ProductSubmitButton-"]) - never builds its own
 *   cart request, so it always submits exactly what the main form holds.
 * - Reveal: throttled rAF scroll listener watching .buy-buttons-block.
 *   No IntersectionObserver, no footer dependency (the native bar's bug).
 * - v2: the variant menu is a custom listbox that always opens UPWARD
 *   (button trigger, not a native select - also avoids mobile auto-zoom).
 */
(function () {
  'use strict';

  var INITED = 'satcInited';

  function initAll() {
    document.querySelectorAll('[data-satc]').forEach(function (root) {
      if (root.dataset[INITED] === '1') return;
      root.dataset[INITED] = '1';
      initBar(root);
    });
  }

  function initBar(root) {
    var pid = root.dataset.productId;
    var reveal = root.dataset.reveal || 'after-scroll';
    var offset = parseInt(root.dataset.offset, 10) || 600;
    var texts = {
      atc: root.dataset.atcText || 'Add to cart',
      sold: root.dataset.soldoutText || 'Sold out',
      pre: root.dataset.preorderText || 'Pre-order to cart'
    };

    var dd = root.querySelector('[data-satc-dd]');
    var ddTrigger = root.querySelector('[data-satc-dd-trigger]');
    var ddLabel = root.querySelector('[data-satc-dd-label]');
    var ddPanel = root.querySelector('[data-satc-dd-panel]');
    var pill = root.querySelector('[data-satc-pill]');
    var pillLabel = root.querySelector('[data-satc-pill-label]');
    var priceCur = root.querySelector('[data-satc-price-cur]');
    var priceNum = root.querySelector('[data-satc-price-num]');
    var priceCmp = root.querySelector('[data-satc-price-cmp]');
    var cmpNum = root.querySelector('[data-satc-cmp-num]');

    /* ---- variant data island ---- */
    var map = {};
    try {
      var island = root.querySelector('[data-satc-data]');
      var parsed = JSON.parse(island.textContent);
      (parsed.variants || []).forEach(function (v) { map[String(v.id)] = v; });
    } catch (e) {
      /* no island -> bar still works as a dumb proxy */
    }

    /* ---- refresh the bar from a variant id (mirror only) ---- */
    function refresh(vid) {
      vid = String(vid);
      var v = map[vid];
      if (!v) return;
      if (ddLabel && v.t) ddLabel.textContent = v.t;
      if (ddPanel) {
        ddPanel.querySelectorAll('.satc__dd-item').forEach(function (it) {
          it.classList.toggle('is-selected', it.dataset.variantId === vid);
        });
      }
      if (priceNum) priceNum.textContent = v.p;
      if (priceCur) priceCur.classList.toggle('satc__price-current--sale', !!v.c);
      if (priceCmp) {
        priceCmp.classList.toggle('satc__hidden', !v.c);
        if (cmpNum) cmpNum.textContent = v.c || '';
      }
      if (pill) {
        pill.disabled = !v.av;
        if (pillLabel) {
          pillLabel.textContent = !v.av ? texts.sold : (v.po ? texts.pre : texts.atc);
        }
      }
    }

    /* ---- read the LIVE hidden variant input (source of truth) ---- */
    function readMainVariantId() {
      var inp = document.querySelector(
        'product-form-component[data-product-id="' + pid + '"] input[name="id"]'
      );
      return inp ? String(inp.value) : null;
    }

    /* ---- custom up-opening dropdown ---- */
    function ddSetOpen(open) {
      if (!dd || !ddPanel || !ddTrigger) return;
      dd.dataset.open = open ? '1' : '0';
      ddPanel.hidden = !open;
      ddTrigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    function driveMain(vid) {
      var input = document.querySelector(
        'variant-picker[data-product-id="' + pid + '"] input[data-variant-id="' + vid + '"]'
      );
      if (input) {
        if (!input.checked) input.click();
      } else {
        /* main picker may be in dropdown mode */
        var opt = document.querySelector(
          'variant-picker[data-product-id="' + pid + '"] select option[data-variant-id="' + vid + '"]'
        );
        if (opt) {
          var sel = opt.closest('select');
          if (sel && sel.value !== opt.value) {
            sel.value = opt.value;
            sel.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      }
      refresh(vid); /* optimistic; variantUpdate confirms */
    }

    if (dd && ddTrigger && ddPanel) {
      ddTrigger.addEventListener('click', function (e) {
        e.stopPropagation();
        ddSetOpen(ddPanel.hidden);
      });
      ddPanel.addEventListener('click', function (e) {
        var item = e.target.closest('.satc__dd-item');
        if (!item) return;
        e.stopPropagation();
        ddSetOpen(false);
        driveMain(item.dataset.variantId);
      });
      ddPanel.addEventListener('keydown', function (e) {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        var item = e.target.closest('.satc__dd-item');
        if (!item) return;
        e.preventDefault();
        ddSetOpen(false);
        driveMain(item.dataset.variantId);
      });
      document.addEventListener('click', function (e) {
        if (!ddPanel.hidden && !dd.contains(e.target)) ddSetOpen(false);
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') ddSetOpen(false);
      });
    }

    /* ---- add to cart: proxy-click the main button (fresh query) ---- */
    if (pill) {
      pill.addEventListener('click', function () {
        var scope = 'product-form-component[data-product-id="' + pid + '"] ';
        // Priority: 1) native buy-buttons submit, 2) bundle ATC [data-asb-atc] if enabled,
        // 3) smooth-scroll to the bundle root (slots not filled yet), 4) generic submit fallback.
        var native = document.querySelector(scope + 'button[id^="BuyButtons-ProductSubmitButton-"]');
        if (native && !native.disabled) { native.click(); return; }
        var bundleAtc = document.querySelector('[data-asb-atc]');
        if (bundleAtc && !bundleAtc.disabled) { bundleAtc.click(); return; }
        var bundleRoot = document.querySelector('[data-asb]');
        if (bundleRoot) { bundleRoot.scrollIntoView({ behavior: 'smooth', block: 'center' }); return; }
        var fallback = document.querySelector(scope + 'button[type="submit"]');
        if (fallback && !fallback.disabled) fallback.click();
      });
    }

    /* ---- main -> sticky: delegated sync (survives section re-renders) ---- */
    function onVariantEvent(evt) {
      var d = evt.detail || {};
      var vid =
        (d.resource && d.resource.id) ||
        (d.variant && d.variant.id) ||
        d.variantId ||
        d.id ||
        null;
      if (vid && map[String(vid)]) {
        refresh(vid);
      } else {
        /* fall back to the live hidden input once the re-render settles */
        setTimeout(function () {
          var live = readMainVariantId();
          if (live && map[live]) refresh(live);
        }, 80);
      }
    }
    document.addEventListener('variantUpdate', onVariantEvent);
    document.addEventListener('variantSelected', onVariantEvent);

    /* ---- reveal ---- */
    var forcedByEditor = false;

    function computeVisible() {
      if (reveal === 'always') return true;
      var anchor = document.querySelector('.buy-buttons-block') || document.querySelector('[data-asb]');
      if (anchor) {
        return anchor.getBoundingClientRect().bottom < 0;
      }
      return (window.scrollY || window.pageYOffset || 0) > offset;
    }

    function apply() {
      root.classList.toggle('is-visible', forcedByEditor || computeVisible());
    }

    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        ticking = false;
        apply();
      });
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    apply();

    /* ---- theme editor niceties ---- */
    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:section:select', function (e) {
        if (e.detail && e.detail.sectionId === root.dataset.sectionId) {
          forcedByEditor = true;
          apply();
        }
      });
      document.addEventListener('shopify:section:deselect', function (e) {
        if (e.detail && e.detail.sectionId === root.dataset.sectionId) {
          forcedByEditor = false;
          apply();
        }
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', initAll);
})();
